import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AssistantContextSnapshot, AssistantPageState } from './assistantContextTypes';
import { normalizePageState } from './normalize';

/**
 * App-level assistant context provider.
 *
 * Pages publish stable SEMANTIC state; the assistant reads a normalized
 * snapshot. Neither side imports the other's React context, which is what lets
 * a future app-wide assistant shell consume Forecast V3, Budget, or Income
 * Journal without knowing anything about them.
 *
 * Two things it deliberately does not do:
 *
 *   - no financial calculation. It carries references, never values.
 *   - no network observation. Knowing that a component *called an endpoint* is
 *     a weaker and more fragile signal than knowing the semantic state the page
 *     chose to display, and it would sweep up response payloads as a side
 *     effect.
 *
 * Registration is keyed by owner so a page that unmounts clears only its own
 * context — stale context must never leak into the next page.
 */

/**
 * What changed, so a page can decide whether it cares. A page showing August
 * should not refetch because July moved, and a page for another property should
 * never refetch at all.
 */
export interface DataChange {
  /** Proposal kinds that were applied, e.g. ['journal_adjustment']. */
  kinds: string[];
  locationId?: number | null;
  /** Business dates touched, ISO, for day-scoped writes. */
  dates?: string[];
  /** Forecast version, for version-scoped writes. */
  versionId?: number | null;
  /** The run that produced the change, for logging and dedupe. */
  workflowRunId?: string | null;
}

interface ServerContextFeedback {
  /** The resolved snapshot the server actually used, if it returned one. */
  entities?: { selection?: { id?: number } | null } | null;
  limitations?: string[] | null;
}

interface AssistantContextValue {
  snapshot: AssistantContextSnapshot | null;
  /** Set when the server dropped part of the context it was sent. */
  warning: string | null;
  publish: (ownerId: string, state: AssistantPageState | null) => void;
  clear: (ownerId: string) => void;
  /** Drop the row/account selection but keep property/version/period scope. */
  clearSelection: () => void;
  /**
   * Reconcile with what the server actually resolved. When a selection we sent
   * did not come back, it was deleted, unauthorized, or stale — drop it and say
   * so, rather than letting the next question silently analyze a different row.
   */
  applyServerFeedback: (resolved: ServerContextFeedback | null, sentHash?: string | null) => void;
  /**
   * Register what to re-read when the assistant changes data this page shows.
   *
   * The context channel runs page → assistant. This is the return leg. Without
   * it an applied change is invisible: the assistant reports an adjustment
   * written and the Income Journal keeps showing the balance it read before,
   * so the user is looking at a figure the database no longer holds. Returns
   * an unsubscribe, and pages register on mount like any other subscription.
   */
  onDataChanged: (handler: (change: DataChange) => void) => () => void;
  /**
   * Announce that data changed. Called by the apply path after a write is
   * confirmed — never optimistically, because a refresh that runs before the
   * write lands re-reads the old value and looks like the write failed.
   */
  notifyDataChanged: (change: DataChange) => void;
}

const AssistantContext = createContext<AssistantContextValue>({
  snapshot: null,
  warning: null,
  publish: () => {},
  clear: () => {},
  clearSelection: () => {},
  applyServerFeedback: () => {},
  onDataChanged: () => () => {},
  notifyDataChanged: () => {},
});

export function AssistantContextProvider({ children }: { children: React.ReactNode }) {
  const [snapshot, setSnapshot] = useState<AssistantContextSnapshot | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Which page owns the current context, and the raw state it published.
  const ownerRef = useRef<string | null>(null);
  const stateRef = useRef<AssistantPageState | null>(null);
  const hashRef = useRef<string | null>(null);

  const applyState = useCallback((state: AssistantPageState | null) => {
    stateRef.current = state;
    const next = normalizePageState(state);
    // Only re-render when the semantic state actually changed. capturedAt is
    // outside the hash precisely so an unchanged page does not churn.
    if (next?.hash === hashRef.current) return;
    hashRef.current = next?.hash ?? null;
    setSnapshot(next);
  }, []);

  const publish = useCallback((ownerId: string, state: AssistantPageState | null) => {
    if (!state) {
      if (ownerRef.current === ownerId) {
        ownerRef.current = null;
        applyState(null);
      }
      return;
    }
    ownerRef.current = ownerId;
    applyState(state);
  }, [applyState]);

  const clear = useCallback((ownerId: string) => {
    // A late unmount from a page that no longer owns the context must not wipe
    // the page that replaced it.
    if (ownerRef.current !== ownerId) return;
    ownerRef.current = null;
    applyState(null);
  }, [applyState]);

  const dropSelection = useCallback(() => {
    const current = stateRef.current;
    if (!current || !current.selection) return false;
    applyState({ ...current, selection: null, labelParts: (current.labelParts || []).slice(0, -1) });
    return true;
  }, [applyState]);

  // A Set rather than state: subscribing must not re-render the provider, or
  // every page mount would ripple through the whole tree.
  const dataListenersRef = useRef<Set<(change: DataChange) => void>>(new Set());

  const onDataChanged = useCallback((handler: (change: DataChange) => void) => {
    dataListenersRef.current.add(handler);
    return () => { dataListenersRef.current.delete(handler); };
  }, []);

  const notifyDataChanged = useCallback((change: DataChange) => {
    for (const handler of Array.from(dataListenersRef.current)) {
      // One page's failed refetch must not stop the others from refreshing.
      try { handler(change); } catch { /* a listener's own problem */ }
    }
  }, []);

  const clearSelection = useCallback(() => {
    setWarning(null);
    dropSelection();
  }, [dropSelection]);

  const applyServerFeedback = useCallback((resolved: ServerContextFeedback | null, sentHash?: string | null) => {
    if (!resolved) return;
    // Feedback belongs to the turn that asked. The assistant is ambient and can
    // be minimized, so a user routinely navigates while a turn is running — and
    // this used to compare a finished turn's answer against whatever page they
    // had reached, dropping a selection or warning about a page that turn never
    // saw. When the caller names the context it sent, stale feedback is ignored.
    if (sentHash && hashRef.current && sentHash !== hashRef.current) return;
    const sentSelection = stateRef.current?.selection ?? null;
    const resolvedSelection = resolved.entities?.selection ?? null;

    if (sentSelection && !resolvedSelection) {
      // The server rehydrated everything else but not the selected row.
      const reason = (resolved.limitations || []).find(l => /select/i.test(l))
        || 'The selected item is no longer available, so it was dropped from the assistant context.';
      dropSelection();
      setWarning(reason);
      return;
    }
    // Anything else the server dropped is worth surfacing once, without
    // touching scope.
    const other = (resolved.limitations || []).find(l => /ignored|dropped|not visible/i.test(l));
    setWarning(other || null);
  }, [dropSelection]);

  const value = useMemo(
    () => ({ snapshot, warning, publish, clear, clearSelection, applyServerFeedback, onDataChanged, notifyDataChanged }),
    [snapshot, warning, publish, clear, clearSelection, applyServerFeedback, onDataChanged, notifyDataChanged],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

/** Read-only access for the assistant surfaces. */
export function useAssistantContext(): AssistantContextValue {
  return useContext(AssistantContext);
}

export default AssistantContextProvider;
